import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "sonner";
import { apiClient } from "../../lib/api";

// --- Status Definitions ---
export const UploadStatuses = {
  PENDING_UPLOAD: "PENDING_UPLOAD",
  UPLOADING: "UPLOADING",
  UPLOADED: "UPLOADED",
  PROCESSING_STARTED: "PROCESSING_STARTED",
  PROCESSING_CPP: "PROCESSING_CPP",
  STORING_S3: "STORING_S3",
  COMPLETED: "COMPLETED",
  FAILED_TRANSFER: "FAILED_TRANSFER",
  FAILED_PROCESSING: "FAILED_PROCESSING",
  FAILED_CPP: "FAILED_CPP",
  FAILED_S3: "FAILED_S3",
} as const;

export type UploadStatus = (typeof UploadStatuses)[keyof typeof UploadStatuses];

// --- State Shape ---
export interface UploadJobState {
  uploadId: string; // Can be a temp ID initially, then backend upload ID
  tempUploadId?: string; // Store the initial temp ID for lookup
  fileName: string;
  transferProgress: number;
  status: UploadStatus;
  statusDetails: string | null;
  s3ObjectKey: string | null;
  error: string | null;
  /** True when this job was hydrated from DB as UPLOADING and transfer is not currently active in this tab. */
  isInterrupted?: boolean;
  /** True when this job entry was populated from the server on page load (not created by a live transfer event). */
  fromHydration?: boolean;
}

interface MultipartInitResponse {
  uploadId: string;
  objectKey: string;
  multipartUploadId: string;
  partSizeBytes: number;
  partUrls: Array<{ partNumber: number; url: string }>;
  expiresInSeconds: number;
}

interface MultipartSignPartsResponse {
  uploadId: string;
  partUrls: Array<{ partNumber: number; url: string }>;
  expiresInSeconds: number;
}

interface MultipartResumeRecord {
  uploadId: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  objectKey: string;
  multipartUploadId: string;
  partSizeBytes: number;
  uploadedParts: Record<string, string>;
}

interface MultipartRuntime {
  cancelled: boolean;
  activeXhrs: Set<XMLHttpRequest>;
}

interface UploadJobStateFromServer {
  UploadID: string;
  OriginalFilename: string;
  Status: UploadStatus;
  StatusDetails: string | null;
  S3ObjectKey: string | null;
  UpdatedAt: string;
  CreatedAt: string;
  UserID: string;
}

interface UploadState {
  jobs: Record<string, UploadJobState>;
  isLoadingPending: boolean; // Flag for loading pending jobs
  initialLoadComplete: boolean; // Flag to prevent multiple initial fetches
}

const initialState: UploadState = {
  jobs: {},
  isLoadingPending: false,
  initialLoadComplete: false,
};

// --- Dismissed uploads (persisted in localStorage) ---
const DISMISSED_KEY = "insaion_dismissed_upload_ids";

function getDismissedIds(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function addDismissedId(uploadId: string): void {
  try {
    const ids = getDismissedIds();
    ids.add(uploadId);
    // Cap at 200 entries to avoid unbounded growth
    const trimmed = Array.from(ids).slice(-200);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(trimmed));
  } catch {
    // localStorage unavailable — ignore
  }
}

// --- Helper ---
// Gets or creates a job entry in the state map
// Now uses tempId for initial creation if real ID isn't known
const getOrCreateJob = (
  state: UploadState,
  id: string,
  isTemporary: boolean,
): UploadJobState => {
  if (!state.jobs[id]) {
    state.jobs[id] = {
      uploadId: id, // Initially might be temp ID
      tempUploadId: isTemporary ? id : undefined, // Store temp ID if creating with it
      fileName: "Unknown",
      transferProgress: 0,
      status: UploadStatuses.PENDING_UPLOAD,
      statusDetails: null,
      s3ObjectKey: null,
      error: null,
    };
  }
  return state.jobs[id];
};

// --- Environment Variables ---
const MULTIPART_RECORDS_KEY = "insaion_multipart_records_v1";
const MULTIPART_FINGERPRINTS_KEY = "insaion_multipart_fingerprints_v1";
const MAX_PART_URL_BATCH = 32;
const PART_UPLOAD_CONCURRENCY = 4;
const runtimeUploads = new Map<string, MultipartRuntime>();

const buildMultipartFingerprint = (fileObj: File): string =>
  `multipart-${fileObj.name}-${fileObj.type}-${fileObj.size}-${fileObj.lastModified}`;

const getPartByteSize = (
  partNumber: number,
  totalParts: number,
  partSizeBytes: number,
  totalSize: number,
): number => {
  if (partNumber < totalParts) return partSizeBytes;
  const consumed = partSizeBytes * (totalParts - 1);
  return Math.max(0, totalSize - consumed);
};

const readMultipartRecords = (): Record<string, MultipartResumeRecord> => {
  try {
    const raw = localStorage.getItem(MULTIPART_RECORDS_KEY);
    return raw
      ? (JSON.parse(raw) as Record<string, MultipartResumeRecord>)
      : {};
  } catch {
    return {};
  }
};

const writeMultipartRecords = (
  records: Record<string, MultipartResumeRecord>,
): void => {
  try {
    localStorage.setItem(MULTIPART_RECORDS_KEY, JSON.stringify(records));
  } catch {
    // Ignore storage unavailability.
  }
};

const readMultipartFingerprintMap = (): Record<string, string> => {
  try {
    const raw = localStorage.getItem(MULTIPART_FINGERPRINTS_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
};

const writeMultipartFingerprintMap = (map: Record<string, string>): void => {
  try {
    localStorage.setItem(MULTIPART_FINGERPRINTS_KEY, JSON.stringify(map));
  } catch {
    // Ignore storage unavailability.
  }
};

const saveMultipartResumeRecord = (
  fingerprint: string,
  record: MultipartResumeRecord,
): void => {
  const records = readMultipartRecords();
  records[record.uploadId] = record;
  writeMultipartRecords(records);

  const fpMap = readMultipartFingerprintMap();
  fpMap[fingerprint] = record.uploadId;
  writeMultipartFingerprintMap(fpMap);
};

const getMultipartResumeRecord = (
  fingerprint: string,
): MultipartResumeRecord | null => {
  const fpMap = readMultipartFingerprintMap();
  const uploadId = fpMap[fingerprint];
  if (!uploadId) return null;
  const records = readMultipartRecords();
  return records[uploadId] ?? null;
};

const clearMultipartResumeRecord = (uploadId: string): void => {
  const records = readMultipartRecords();
  const record = records[uploadId];
  if (record) {
    delete records[uploadId];
    writeMultipartRecords(records);

    const fpMap = readMultipartFingerprintMap();
    Object.keys(fpMap).forEach((fp) => {
      if (fpMap[fp] === uploadId) {
        delete fpMap[fp];
      }
    });
    writeMultipartFingerprintMap(fpMap);
  }
};

const parseMultipartDetails = (
  details: string | null | undefined,
): { multipartUploadId: string; partSizeBytes: number } | null => {
  if (!details) return null;
  try {
    const parsed = JSON.parse(details) as {
      multipartUploadId?: string;
      partSizeBytes?: number;
    };
    if (!parsed.multipartUploadId || !parsed.partSizeBytes) return null;
    return {
      multipartUploadId: parsed.multipartUploadId,
      partSizeBytes: parsed.partSizeBytes,
    };
  } catch {
    return null;
  }
};

const requestMultipartPartURLs = async (
  uploadId: string,
  partNumbers: number[],
): Promise<Map<number, string>> => {
  const resp = await apiClient.post<MultipartSignPartsResponse>(
    "/logs/uploads/multipart/parts",
    { uploadId, partNumbers },
  );
  const urlMap = new Map<number, string>();
  resp.data.partUrls.forEach((p) => urlMap.set(p.partNumber, p.url));
  return urlMap;
};

const extractApiErrorMessage = (error: any, fallback: string): string => {
  const apiError = error?.response?.data?.error;
  if (typeof apiError === "string" && apiError.trim() !== "") {
    return apiError;
  }

  const apiMessage = error?.response?.data?.message;
  if (typeof apiMessage === "string" && apiMessage.trim() !== "") {
    return apiMessage;
  }

  // Guardrail for cases where backend responds with 409 but no explicit message.
  if (error?.response?.status === 409) {
    return "Storage limit exceeded";
  }

  if (typeof error?.message === "string" && error.message.trim() !== "") {
    return error.message;
  }

  return fallback;
};

const uploadPartWithProgress = (
  file: File,
  partNumber: number,
  partSizeBytes: number,
  totalParts: number,
  signedURL: string,
  runtime: MultipartRuntime,
  onProgress: (uploadedBytesForPart: number) => void,
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const partStart = (partNumber - 1) * partSizeBytes;
    const partByteSize = getPartByteSize(
      partNumber,
      totalParts,
      partSizeBytes,
      file.size,
    );
    const partEnd = partStart + partByteSize;
    const blob = file.slice(partStart, partEnd);

    const xhr = new XMLHttpRequest();
    runtime.activeXhrs.add(xhr);

    xhr.open("PUT", signedURL, true);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded);
      }
    };

    xhr.onerror = () => {
      runtime.activeXhrs.delete(xhr);
      reject(
        new Error(`Part ${partNumber} upload failed due to network error`),
      );
    };

    xhr.onabort = () => {
      runtime.activeXhrs.delete(xhr);
      reject(new Error("Upload cancelled by user"));
    };

    xhr.onload = () => {
      runtime.activeXhrs.delete(xhr);
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag =
          xhr.getResponseHeader("ETag") || xhr.getResponseHeader("etag");
        if (!etag) {
          reject(
            new Error(
              `Part ${partNumber} uploaded but missing ETag response header`,
            ),
          );
          return;
        }
        resolve(etag.replace(/"/g, ""));
        return;
      }
      reject(
        new Error(`Part ${partNumber} upload failed with status ${xhr.status}`),
      );
    };

    xhr.send(blob);
  });
};

export const fetchPendingUploads = createAsyncThunk(
  "upload/fetchPending",
  async (_, { rejectWithValue, getState }) => {
    // Use generic RootState type or cast getState() result
    const state = getState() as { uploadFileReducer: UploadState };
    if (state.uploadFileReducer.initialLoadComplete) {
      return [] as UploadJobStateFromServer[];
    }
    try {
      // Call the backend endpoint using axios
      const response = await apiClient.get<UploadJobStateFromServer[]>(
        "/upload-jobs?status=pending", // Use correct endpoint
      );
      return response.data; // Return the array of job states
    } catch (error: any) {
      console.error("Failed to fetch pending uploads:", error);
      const errorMsg =
        error.response?.data?.error ||
        error.message ||
        "Failed to fetch pending uploads";
      return rejectWithValue({
        message: errorMsg,
        status: error.response?.status as number | undefined,
      });
    }
  },
);

export const uploadFile = createAsyncThunk(
  "uploadFile/uploadWithMultipart",
  async (
    { file, userId }: { file: File; userId: string },
    { dispatch, signal, rejectWithValue },
  ): Promise<{ uploadId: string; finalUrl: string }> => {
    // Generate temp ID for initial state tracking
    const tempUploadId = `temp-${Date.now()}-${file.name}`;
    const fingerprint = buildMultipartFingerprint(file);
    const _userId = userId;
    void _userId;
    let activeUploadId: string | null = null;

    dispatch(
      uploadTransferStarted({
        uploadId: tempUploadId,
        fileName: file.name,
      }),
    );

    try {
      let resumeRecord = getMultipartResumeRecord(fingerprint);
      let uploadId = "";
      let objectKey = "";
      let multipartUploadId = "";
      let partSizeBytes = 0;

      if (resumeRecord && resumeRecord.fileSize === file.size) {
        try {
          const statusResp = await apiClient.get(
            `/logs/uploads/multipart/${resumeRecord.uploadId}`,
          );
          const status =
            (statusResp.data?.Status as string | undefined) ||
            (statusResp.data?.status as string | undefined);
          if (status === UploadStatuses.UPLOADING) {
            const parsedDetails = parseMultipartDetails(
              statusResp.data?.StatusDetails || statusResp.data?.statusDetails,
            );
            const resolvedObjectKey =
              statusResp.data?.S3ObjectKey || statusResp.data?.s3ObjectKey;
            if (parsedDetails && resolvedObjectKey) {
              uploadId = resumeRecord.uploadId;
              objectKey = resolvedObjectKey;
              multipartUploadId = parsedDetails.multipartUploadId;
              partSizeBytes = parsedDetails.partSizeBytes;
              resumeRecord = {
                ...resumeRecord,
                objectKey,
                multipartUploadId,
                partSizeBytes,
              };
              saveMultipartResumeRecord(fingerprint, resumeRecord);
            } else {
              resumeRecord = null;
            }
          } else {
            clearMultipartResumeRecord(resumeRecord.uploadId);
            resumeRecord = null;
          }
        } catch {
          resumeRecord = null;
        }
      }

      if (!resumeRecord) {
        const initResp = await apiClient.post<MultipartInitResponse>(
          "/logs/uploads/multipart/init",
          {
            filename: file.name,
            fileSizeBytes: file.size,
            contentType: file.type || "application/octet-stream",
            initialPartCount: 8,
          },
        );

        uploadId = initResp.data.uploadId;
        objectKey = initResp.data.objectKey;
        multipartUploadId = initResp.data.multipartUploadId;
        partSizeBytes = initResp.data.partSizeBytes;

        resumeRecord = {
          uploadId,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          objectKey,
          multipartUploadId,
          partSizeBytes,
          uploadedParts: {},
        };
        saveMultipartResumeRecord(fingerprint, resumeRecord);
      }

      uploadId = resumeRecord.uploadId;
      partSizeBytes = resumeRecord.partSizeBytes;

      const runtime: MultipartRuntime = {
        cancelled: false,
        activeXhrs: new Set(),
      };
      runtimeUploads.set(uploadId, runtime);
      activeUploadId = uploadId;

      signal.addEventListener("abort", () => {
        runtime.cancelled = true;
        runtime.activeXhrs.forEach((xhr) => xhr.abort());
      });

      dispatch(
        uploadProgressUpdated({
          uploadId,
          tempUploadId,
          progress: 0,
        }),
      );

      const totalParts = Math.ceil(file.size / partSizeBytes);
      const uploadedParts = { ...resumeRecord.uploadedParts };
      const alreadyUploadedPartNumbers = Object.keys(uploadedParts)
        .map((k) => Number(k))
        .filter((n) => Number.isInteger(n) && n > 0);

      let bytesAlreadyUploaded = alreadyUploadedPartNumbers.reduce(
        (sum, partNo) =>
          sum + getPartByteSize(partNo, totalParts, partSizeBytes, file.size),
        0,
      );
      const inFlightPartBytes = new Map<number, number>();

      const dispatchProgress = (): void => {
        const inFlightTotal = Array.from(inFlightPartBytes.values()).reduce(
          (acc, value) => acc + value,
          0,
        );
        const totalUploaded = Math.min(
          file.size,
          bytesAlreadyUploaded + inFlightTotal,
        );
        const pct = Math.round((totalUploaded / file.size) * 100);
        dispatch(
          uploadProgressUpdated({
            uploadId,
            tempUploadId,
            progress: pct,
          }),
        );
      };

      const missingParts: number[] = [];
      for (let part = 1; part <= totalParts; part += 1) {
        if (!uploadedParts[String(part)]) {
          missingParts.push(part);
        }
      }

      dispatchProgress();

      for (
        let offset = 0;
        offset < missingParts.length;
        offset += MAX_PART_URL_BATCH
      ) {
        if (runtime.cancelled) {
          throw new Error("Upload cancelled by user");
        }

        const batchParts = missingParts.slice(
          offset,
          offset + MAX_PART_URL_BATCH,
        );
        const partUrlMap = await requestMultipartPartURLs(uploadId, batchParts);

        let cursor = 0;
        const workers = Array.from({ length: PART_UPLOAD_CONCURRENCY }).map(
          async () => {
            while (cursor < batchParts.length) {
              const currentIndex = cursor;
              cursor += 1;
              const partNumber = batchParts[currentIndex];
              if (runtime.cancelled) {
                throw new Error("Upload cancelled by user");
              }
              const signedURL = partUrlMap.get(partNumber);
              if (!signedURL) {
                throw new Error(`Missing signed URL for part ${partNumber}`);
              }

              const etag = await uploadPartWithProgress(
                file,
                partNumber,
                partSizeBytes,
                totalParts,
                signedURL,
                runtime,
                (loadedBytesForPart) => {
                  inFlightPartBytes.set(partNumber, loadedBytesForPart);
                  dispatchProgress();
                },
              );

              inFlightPartBytes.delete(partNumber);
              bytesAlreadyUploaded += getPartByteSize(
                partNumber,
                totalParts,
                partSizeBytes,
                file.size,
              );
              uploadedParts[String(partNumber)] = etag;
              saveMultipartResumeRecord(fingerprint, {
                ...resumeRecord!,
                uploadedParts,
              });
              dispatchProgress();
            }
          },
        );

        await Promise.all(workers);
      }

      const completionParts = Object.keys(uploadedParts)
        .map((k) => ({ partNumber: Number(k), etag: uploadedParts[k] }))
        .filter((p) => Number.isInteger(p.partNumber) && p.partNumber > 0)
        .sort((a, b) => a.partNumber - b.partNumber);

      await apiClient.post("/logs/uploads/multipart/complete", {
        uploadId,
        parts: completionParts,
      });

      clearMultipartResumeRecord(uploadId);
      runtimeUploads.delete(uploadId);
      activeUploadId = null;

      dispatch(
        uploadTransferCompleted({
          uploadId,
          tempUploadId,
        }),
      );

      return { uploadId, finalUrl: objectKey || `multipart://${uploadId}` };
    } catch (error: any) {
      const errorMessage = extractApiErrorMessage(
        error,
        "Failed to upload with multipart flow",
      );

      let targetUploadId = tempUploadId;
      const resumed = getMultipartResumeRecord(fingerprint);
      if (resumed?.uploadId) {
        targetUploadId = resumed.uploadId;
      }

      if (activeUploadId) {
        runtimeUploads.delete(activeUploadId);
      }

      dispatch(
        uploadTransferFailed({
          uploadId: targetUploadId,
          error: errorMessage,
        }),
      );
      return rejectWithValue(errorMessage) as never;
    }
  },
);

export const cancelUpload = createAsyncThunk(
  "upload/cancelUpload",
  async (uploadId: string, { rejectWithValue }) => {
    try {
      const runtime = runtimeUploads.get(uploadId);
      if (runtime) {
        runtime.cancelled = true;
        runtime.activeXhrs.forEach((xhr) => xhr.abort());
      }

      try {
        await apiClient.post(`/logs/uploads/multipart/abort`, { uploadId });
      } catch {
        await apiClient.post(`/upload-jobs/${uploadId}/cancel`);
      }

      clearMultipartResumeRecord(uploadId);
      runtimeUploads.delete(uploadId);
      return uploadId;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.error ||
        error.message ||
        "Failed to cancel upload";
      return rejectWithValue(errorMsg);
    }
  },
);

// --- Slice Definition ---
const uploadSlice = createSlice({
  name: "upload",
  initialState,
  reducers: {
    populatePendingJobs: (
      state,
      action: PayloadAction<UploadJobStateFromServer[]>,
    ) => {
      const dismissed = getDismissedIds();
      action.payload.forEach((jobFromServer) => {
        // Skip jobs the user already dismissed — don't re-show them on refresh
        if (dismissed.has(jobFromServer.UploadID)) return;
        const isTransferInProgress =
          jobFromServer.Status === UploadStatuses.UPLOADING;
        state.jobs[jobFromServer.UploadID] = {
          uploadId: jobFromServer.UploadID,
          fileName: jobFromServer.OriginalFilename,
          status: jobFromServer.Status,
          statusDetails:
            jobFromServer.StatusDetails ||
            (isTransferInProgress
              ? "Transfer interrupted. Re-select the same file to resume."
              : null),
          s3ObjectKey: jobFromServer.S3ObjectKey,
          // Don't show a progress bar for interrupted transfers or completed ones
          transferProgress: isTransferInProgress ? 0 : 100,
          error: null,
          tempUploadId: undefined,
          isInterrupted: isTransferInProgress,
          fromHydration: true,
        };
      });
    },
    uploadTransferStarted: (
      state,
      action: PayloadAction<{ uploadId: string; fileName: string }>,
    ) => {
      const jobId = action.payload.uploadId;
      // Use helper, indicate if ID is temporary
      const job = getOrCreateJob(state, jobId, jobId.startsWith("temp-"));
      job.fileName = action.payload.fileName;
      // Only set to UPLOADING if not already in a final state
      if (
        job.status !== UploadStatuses.COMPLETED &&
        !job.status?.startsWith("FAILED")
      ) {
        job.status = UploadStatuses.UPLOADING;
        job.error = null;
        job.isInterrupted = false; // Transfer is now actively running in this tab.
        // Keep progress if resuming
        job.transferProgress = state.jobs[jobId]?.transferProgress ?? 0;
      } else {
        console.warn(
          `Redux Reducer: uploadTransferStarted ignored for ${jobId} (already completed/failed). Status: ${job.status}`,
        );
      }
    },
    uploadProgressUpdated: (
      state,
      action: PayloadAction<{
        uploadId: string;
        tempUploadId: string;
        progress: number;
      }>,
    ) => {
      const currentId = action.payload.uploadId;
      const tempId = action.payload.tempUploadId;
      let job = state.jobs[currentId]; // Try finding by current ID (might be temp or final)
      const progress = action.payload.progress;

      // If job not found by current ID, AND current ID is NOT the temp ID,
      // it means the ID likely transitioned from temp to final.
      // Try finding the job using the original temp ID.
      if (!job && currentId !== tempId && state.jobs[tempId]) {
        if (state.jobs[currentId]) {
          console.error(
            `Redux Reducer: ID transition conflict! Target ID ${currentId} already exists. Aborting progress update.`,
          );
          return; // Avoid overwriting existing state unexpectedly
        }
        // Copy data from temp entry to new entry under final ID
        state.jobs[currentId] = {
          ...state.jobs[tempId], // Copy existing state
          uploadId: currentId, // Update to the final ID
          tempUploadId: undefined, // Clear temp ID marker
        };
        // Remove the old entry keyed by temp ID
        delete state.jobs[tempId];
        // Now get the reference to the *new* job entry
        job = state.jobs[currentId];
      }

      // Update progress if the job exists and is uploading
      if (job && job.status === UploadStatuses.UPLOADING) {
        job.transferProgress = progress;
      }
    },
    uploadTransferCompleted: (
      state,
      action: PayloadAction<{ uploadId: string; tempUploadId: string }>,
    ) => {
      const finalId = action.payload.uploadId;
      const tempId = action.payload.tempUploadId;
      let job = state.jobs[finalId];

      // Handle case where completion event arrives before last progress update renamed the key
      if (!job && state.jobs[tempId]) {
        console.warn(
          `Redux Reducer: Completing job found under temp ID ${tempId}, transitioning to ${finalId}`,
        );
        state.jobs[finalId] = {
          ...state.jobs[tempId],
          uploadId: finalId,
          tempUploadId: undefined,
        };
        delete state.jobs[tempId];
        job = state.jobs[finalId];
      }

      if (job) {
        job.status = UploadStatuses.UPLOADED;
        job.transferProgress = 100;
        job.statusDetails = "Waiting for backend processing...";
        job.error = null;
      } else {
        console.warn(
          `Redux Reducer: Cannot mark transfer complete. Job not found for ID: ${finalId} or TempID: ${tempId}`,
        );
      }
    },
    uploadTransferFailed: (
      state,
      action: PayloadAction<{ uploadId: string | null; error: string }>,
    ) => {
      const uploadId = action.payload.uploadId; // This might be temp or final ID depending on when error occurred
      let job = uploadId ? state.jobs[uploadId] : null;

      // If error reported with temp ID, but state might have transitioned, try finding via tempUploadId field
      if (!job && uploadId?.startsWith("temp-")) {
        job =
          Object.values(state.jobs).find((j) => j.tempUploadId === uploadId) ??
          null;
      }

      if (job) {
        job.status = UploadStatuses.FAILED_TRANSFER;
        job.error = action.payload.error;
        job.statusDetails = "File transfer failed.";
      } else {
        console.error(
          "Upload Transfer Failed (no specific job state found):",
          action.payload.error,
        );
        // TODO: Maybe add a general error state field?
      }
    },
    // --- updateJobStatus, clearUploadJob, resetUploadState remain the same ---
    updateJobStatus: (state, action: PayloadAction<any>) => {
      // Use specific type if broker defined
      const { uploadId, status, details, s3ObjectKey } = action.payload;
      const job = getOrCreateJob(state, uploadId, false);
      if (!job.fileName || job.fileName === "Unknown") {
        job.fileName = `Upload ${uploadId}`;
      }
      job.status = status as UploadStatus;
      job.statusDetails = details || null;
      job.s3ObjectKey = s3ObjectKey || null;
      job.error = null; // Clear hydration flag once the job receives a live SSE update
      job.fromHydration = false;
    },
    clearUploadJob: (state, action: PayloadAction<{ uploadId: string }>) => {
      const id = action.payload.uploadId;
      addDismissedId(id);
      delete state.jobs[id];
    },
    resetUploadState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPendingUploads.pending, (state) => {
        state.isLoadingPending = true; // Set loading flag
      })
      .addCase(fetchPendingUploads.fulfilled, (state, action) => {
        // Call the regular reducer to populate the state
        uploadSlice.caseReducers.populatePendingJobs(state, action);
        // Mark loading finished and initial load complete
        state.isLoadingPending = false;
        state.initialLoadComplete = true;
      })
      .addCase(fetchPendingUploads.rejected, (state, action) => {
        state.isLoadingPending = false;
        state.initialLoadComplete = true; // Mark as complete even if failed to prevent retries
        const rejection = action.payload as
          | { message: string; status?: number }
          | undefined;
        const message = rejection?.message || action.error.message;
        console.error("Fetching pending uploads rejected:", message);
        // A 401 means the session itself was rejected; SubscriptionStatusCheck
        // ends it. Toasting here too repeated the toast on every re-login.
        if (rejection?.status !== 401) {
          toast.error("Status Error", {
            description: `Could not load pending upload statuses: ${message}`,
          });
        }
      })
      .addCase(uploadFile.rejected, (_, action) => {
        console.error(
          "Upload thunk rejected:",
          action.payload || action.error.message,
        );
        const fallbackError = action.error.message || "";
        const description =
          (action.payload as string) ||
          (fallbackError.includes("status code 409")
            ? "Storage limit exceeded"
            : fallbackError) ||
          "Unknown error during initiation/transfer";
        // Toast now handled by uploadTransferFailed reducer
        toast.error("Upload Failed", {
          description,
        });
      })
      .addCase(cancelUpload.fulfilled, (state, action) => {
        const uploadId = action.payload;
        const job = state.jobs[uploadId];
        if (job) {
          job.status = UploadStatuses.FAILED_TRANSFER;
          job.statusDetails = "Upload cancelled by user";
          job.error = "Upload cancelled by user";
        }
      })
      .addCase(cancelUpload.rejected, (_, action) => {
        toast.error("Cancel Failed", {
          description:
            (action.payload as string) ||
            action.error.message ||
            "Could not cancel upload",
        });
      });
  },
});

export const {
  uploadTransferStarted,
  uploadProgressUpdated,
  uploadTransferCompleted,
  uploadTransferFailed,
  updateJobStatus,
  clearUploadJob,
  resetUploadState,
} = uploadSlice.actions;
export const uploadFileReducer = uploadSlice.reducer;
